import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Reveal from "@/components/base/Reveal";
import RelatedFilms from "@/pages/film-detail/components/RelatedFilms";
import { useFavorites } from "@/hooks/useFavorites";
import { useHistory } from "@/hooks/useHistory";
import { films } from "@/mocks/films";
import { screenings } from "@/mocks/screenings";
import type { Film, Screening } from "@/types/content";

export default function FilmDetail() {
  const { id } = useParams<{ id: string }>();
  const film = (films as Film[]).find((item) => item.id === id);
  const { toggle, isFavorite } = useFavorites();
  const { add } = useHistory();
  const [toast, setToast] = useState("");

  usePageMeta(
    film ? `${film.title}-球幕影院沉浸式体验介绍` : "影片未找到-球幕影院",
    film ? `《${film.title}》${film.synopsis}` : "该影片可能已下架，返回影片列表查看更多沉浸式作品。",
    film ? `${film.title},球幕影院,沉浸式电影,${film.genres.join(",")}` : "球幕影院,影片详情",
  );

  useEffect(() => {
    if (film) add(film.id);
  }, [film, add]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!film) {
    return (
      <div className="w-full pt-20 min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <i className="ri-movie-2-line text-4xl text-foreground-500" />
          <h1 className="font-heading font-semibold text-xl text-foreground-950 mt-4 mb-3">
            未找到该影片
          </h1>
          <p className="text-sm text-foreground-600 mb-6">影片可能已下架，去看看其他沉浸式作品吧</p>
          <Link
            to="/films"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-label hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回影片列表
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(film.id);
  const filmScreenings = (screenings as Screening[]).filter((item) => item.filmId === film.id);

  const handleFavorite = () => {
    toggle(film.id);
    setToast(favorited ? "已取消收藏" : "已加入收藏，仅保存在本机");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="w-full pt-20">
      <section className="relative w-full">
        <div className="absolute inset-0 h-[380px] overflow-hidden">
          <img
            src={film.poster}
            alt={`${film.title} 背景`}
            className="w-full h-full object-cover object-top blur-sm opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 to-background-50" />
        </div>

        <div className="relative max-w-content mx-auto px-4 md:px-8 pt-8 pb-12">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/films" className="hover:text-accent-500 transition-colors cursor-pointer">
              影片分类
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{film.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 lg:gap-12">
            <div className="w-full max-w-[320px] mx-auto lg:mx-0">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200/70">
                <img
                  src={film.poster}
                  alt={`${film.title} 球幕影院影片海报`}
                  title={`${film.title} 沉浸式影片海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {film.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/films?tag=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-full text-xs font-label bg-secondary-100/90 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">
                {film.title}
              </h1>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600 mb-6">
                <span className="inline-flex items-center gap-1.5 text-accent-500 font-heading font-semibold text-base">
                  <i className="ri-star-fill" />
                  {film.rating.toFixed(1)}
                </span>
                <span>{film.genres.join(" / ")}</span>
                <span>{film.year} 年</span>
                <span>{film.duration} 分钟</span>
              </div>

              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">{film.synopsis}</p>

              <div className="flex flex-wrap items-center gap-3 mb-8">
                <Link
                  to="/screenings"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 font-label font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-ticket-2-line text-lg" />
                  查看放映场次
                </Link>
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-md border font-label transition-colors whitespace-nowrap cursor-pointer ${
                    favorited
                      ? "border-accent-500 bg-accent-500/10 text-accent-500"
                      : "border-background-300/70 text-foreground-900 hover:bg-background-200/60"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {favorited ? "已收藏" : "收藏影片"}
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label="复制分享链接"
                  className="w-12 h-12 flex items-center justify-center rounded-md border border-background-300/70 text-foreground-900 hover:bg-background-200/60 transition-colors cursor-pointer"
                >
                  <i className="ri-link" />
                </button>
                <a
                  href={`https://91hj1.asia/share/share.php?title=${encodeURIComponent(film.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="分享到微博"
                  className="w-12 h-12 flex items-center justify-center rounded-md border border-background-300/70 text-foreground-900 hover:bg-background-200/60 transition-colors cursor-pointer"
                >
                  <i className="ri-weibo-line" />
                </a>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">导演</dt>
                  <dd className="text-sm text-foreground-950">{film.director}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">主演</dt>
                  <dd className="text-sm text-foreground-950">{film.cast.join("、")}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">上映日期</dt>
                  <dd className="text-sm text-foreground-950">{film.releaseDate}</dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500 mb-1">放映技术</dt>
                  <dd className="text-sm text-foreground-950">{film.tags.join(" · ")}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-content mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">影片简介</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10">{film.description}</p>

            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
              预告片 / 观影体验短片
            </h2>
            {film.trailerUrl ? (
              <div className="rounded-lg overflow-hidden bg-background-200 aspect-video">
                <video
                  src={film.trailerUrl}
                  controls
                  poster={film.poster}
                  aria-label={`${film.title} 预告片`}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300/70 bg-background-100 aspect-video flex flex-col items-center justify-center text-center px-6">
                <i className="ri-video-off-line text-3xl text-foreground-500" />
                <p className="text-sm text-foreground-700 mt-4 max-w-sm leading-relaxed">
                  该影片暂未提供在线预告短片。穹顶沉浸影像的最佳效果需要现场体验，
                  欢迎前往影院感受完整版观影效果。
                </p>
              </div>
            )}
          </div>

          <aside>
            <Reveal>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <h2 className="font-heading font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-accent-500" />
                  近期场次
                </h2>
                {filmScreenings.length === 0 ? (
                  <p className="text-sm text-foreground-600">该影片暂无排期，敬请期待。</p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {filmScreenings.slice(0, 4).map((item) => (
                      <li
                        key={item.id}
                        className="flex items-center justify-between gap-3 text-sm pb-3 border-b border-background-200/60 last:border-0 last:pb-0"
                      >
                        <div>
                          <p className="text-foreground-950">
                            {item.date} · {item.time}
                          </p>
                          <p className="text-xs text-foreground-500 mt-0.5">{item.hall}</p>
                        </div>
                        <span className="font-heading font-semibold text-accent-500 whitespace-nowrap">
                          ¥{item.price}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to="/screenings"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-label text-accent-500 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  全部放映动态
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <RelatedFilms film={film} />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}